import { BusinessProfileId } from "@sp-crm/core";
import { ContentFull, ContentHeader } from "components/layout";
import { Breadcrumbs } from "components/ui/breadcrumbs";
import { SecondaryButtonLink } from "components/ui/button-link";
import { InlineBanner } from "components/ui/inline-banner";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useIsMultiRegionUser } from "store/selectors/hooks";
import { BusinessDetails } from "./business-details";
import {
    businessDisplayName,
    businessVerificationPath,
    toBusinessProfileDetails,
} from "./business-profile-types";
import {
    BusinessStatusBadge,
    ContactSupportToChange,
} from "./business-verification-cards";
import {
    BusinessProfileOutput,
    useBusinessProfiles,
    useCoveredOfficesPhrase,
} from "./use-business-verification";

const ProfileView: React.FC<{ profile: BusinessProfileOutput }> = ({ profile }) => {
    const isMultiOffice = useIsMultiRegionUser();
    const coveredOffices = useCoveredOfficesPhrase();
    const details = toBusinessProfileDetails(profile);
    return (
        <ContentFull>
            <div className="max-w-3xl space-y-4">
                <div>
                    <Breadcrumbs
                        links={[
                            {
                                label: "SMS & Voice Business Verification",
                                to: businessVerificationPath,
                            },
                        ]}
                        current={businessDisplayName(details)}
                    />
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        <ContentHeader>{businessDisplayName(details)}</ContentHeader>
                        <BusinessStatusBadge profile={profile} />
                    </div>
                    {isMultiOffice ? (
                        <p className="text-sm text-gray-500 mt-0.5">
                            Covers {coveredOffices(profile.officeIds)}
                        </p>
                    ) : null}
                </div>
                <ContactSupportToChange />
                <BusinessDetails details={details} />
            </div>
        </ContentFull>
    );
};

export const BusinessProfileView: React.FC<{ businessProfileId: BusinessProfileId }> = ({
    businessProfileId,
}) => {
    const { profilesQuery } = useBusinessProfiles();
    const profile = profilesQuery.data?.find(p => p.id === businessProfileId) ?? null;

    if (profilesQuery.isLoading) {
        return <Spinner />;
    }
    if (profilesQuery.isLoadingError) {
        return (
            <ContentFull>
                <RetryableErrorBanner
                    message="We couldn't load this business."
                    retrying={profilesQuery.isFetching}
                    onRetry={() => {
                        profilesQuery.refetch().catch((): void => undefined);
                    }}
                />
            </ContentFull>
        );
    }
    if (!profile) {
        return (
            <ContentFull>
                <div className="space-y-4">
                    <InlineBanner type="info">
                        We couldn&apos;t find this business.
                    </InlineBanner>
                    <SecondaryButtonLink to={businessVerificationPath}>
                        Back to SMS &amp; Voice Business Verification
                    </SecondaryButtonLink>
                </div>
            </ContentFull>
        );
    }
    return <ProfileView profile={profile} />;
};
